import type { LanguageCode, LanguageConfig } from '@/config/types'

let _uiLang: LanguageCode = 'en'
let _apiLang = 'en-us'
let _config: LanguageConfig | undefined

/**
 * Resolves active language once at app boot.
 *
 * Priority: ?lang= URL param → clientConfig.language.defaultLanguage → 'en'
 *
 * URL param is read from the hash fragment because the router uses hash history.
 * e.g. /#/location?key=12345&lang=es  →  hash = '#/location?key=12345&lang=es'
 *
 * Only values listed in supportedLanguages are accepted; anything else falls back.
 */
export function resolveLanguage(config: LanguageConfig | undefined): void {
  _config = config
  const hash = window.location.hash
  const queryStr = hash.includes('?') ? hash.split('?')[1] : ''
  const urlLang = new URLSearchParams(queryStr).get('lang')?.toLowerCase().trim()

  const supported: LanguageCode[] = config?.supportedLanguages ?? ['en']
  const apiMap: Record<string, string> = config?.apiLanguageMap ?? { en: 'en-us', es: 'es' }
  const configured = config?.defaultLanguage as LanguageCode | undefined
  const defaultLang: LanguageCode = configured && supported.includes(configured) ? configured : 'en'

  const fromUrl =
    urlLang && supported.includes(urlLang as LanguageCode) ? (urlLang as LanguageCode) : null

  _uiLang = fromUrl ?? defaultLang
  _apiLang = apiMap[_uiLang] ?? 'en-us'
}

/**
 * Switches to a new language at runtime.
 *
 * Returns the language that ended up active — same as the input on success,
 * or the previous language if the requested code is not in supportedLanguages.
 * Does not touch the i18n instance directly; callers should subscribe via
 * `onLanguageChange` (typically in a single boot site).
 */
export function setLanguage(lang: string): LanguageCode {
  const code = lang.toLowerCase().trim() as LanguageCode
  const supported: LanguageCode[] = _config?.supportedLanguages ?? ['en']
  if (!supported.includes(code)) return _uiLang

  const apiMap: Record<string, string> = _config?.apiLanguageMap ?? { en: 'en-us', es: 'es' }
  _uiLang = code
  _apiLang = apiMap[code] ?? 'en-us'

  _listeners.forEach((fn) => {
    try {
      fn(code)
    } catch (e) {
      console.warn('[i18n] language listener threw:', e)
    }
  })
  return code
}

type LanguageChangeListener = (code: LanguageCode) => void
const _listeners: Set<LanguageChangeListener> = new Set()

/** Subscribe to runtime language changes. Returns an unsubscribe function. */
export function onLanguageChange(fn: LanguageChangeListener): () => void {
  _listeners.add(fn)
  return () => _listeners.delete(fn)
}

/** UI locale code used by vue-i18n (e.g. 'en', 'es'). */
export function getUiLang(): LanguageCode {
  return _uiLang
}

/** API language code forwarded to AccuWeather requests (e.g. 'en-us', 'es'). */
export function getApiLang(): string {
  return _apiLang
}
